/**
 * B545 (d) — the buyer attaches at PAY: the order.buyer.set call for a
 * PENDING order (Cart's Continue checked out with no buyer). The idempotency
 * key follows the DETAILS: a double tap replays the same write, a corrected
 * email is a new one (the door re-binds while the order awaits payment).
 */
import { cartCheckout, policyAcknowledgement } from "@transact/contracts";
import type { ProductSummary } from "../ui/screens.js";

export interface BuyerDetails {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
}

/** The Cart step's Continue: cart.checkout names nobody (the buyer comes at Pay). */
export const NO_BUYER: BuyerDetails = Object.freeze({ name: "", email: "", phone: "" });

/**
 * B594 (2) — cart.checkout's body: the buyer fields only when typed (B415:
 * required by the storefront in words, optional on the wire) and the shell
 * the checkout happened in — the funnel marks' own surface — so the
 * operator's Analytics can say paid per surface.
 */
export function checkoutBody(
  cartId: string,
  buyer: BuyerDetails,
  surface: "hosted" | "inline" | "list" | "dialog",
  /** B615 leg 2 — the pay step's acknowledgement was ticked. */
  acknowledged = false,
  /** B616 leg 3a — the pay step's Insider opt-in was ticked. */
  subscribe = false,
): {
  cartId: string;
  buyerEmail?: string;
  buyerName?: string;
  buyerPhone?: string;
  surface: "hosted" | "inline" | "list" | "dialog";
  acknowledged?: true;
  subscribe?: true;
} {
  const { email, name, phone } = buyer;
  return {
    cartId,
    ...(email !== "" ? { buyerEmail: email } : {}),
    ...(name !== "" ? { buyerName: name } : {}),
    ...(phone !== "" ? { buyerPhone: phone } : {}),
    surface,
    ...(acknowledged ? { acknowledged: true as const } : {}),
    ...(subscribe ? { subscribe: true as const } : {}),
  };
}

/* ------------------------------------------------------------- B616 */

/** B616 leg 3a — the pay step's Insider opt-in, in the club's words (555:78). */
export const OPT_IN_WORDS = "Email me about upcoming shows";

/**
 * Who is asked: a SHOW tenant's buyer (the club's list — the item's scope).
 * Every other tenant's pay step stays as it is until its own words are ruled.
 */
export function optInFor(tenantType: string | undefined): string | undefined {
  return tenantType === "SHOW" ? OPT_IN_WORDS : undefined;
}

/* ------------------------------------------------------------- B615 */

/**
 * B615 leg 2 — the ONE line this cart's buyer ticks on the pay step, from
 * the cart's products' policy facts (the contracts' composer — the same
 * grammar the server records). Undefined = nothing asks: no box is drawn.
 */
export function acknowledgementFor(
  products: readonly ProductSummary[],
  picks: readonly { readonly productId: string; readonly slotId?: string | undefined }[],
  /** B615 leg 3b — slot ages from the page's own slot.list reads (a resumed cart's unread slot falls back to the product's age; the server still records the true line). */
  slotAges: ReadonlyMap<string, number>,
): string | undefined {
  return policyAcknowledgement(
    picks.map((pick) => {
      const policies = products.find((p) => p.productId === pick.productId)?.presentation?.policies;
      const age = pick.slotId !== undefined ? slotAges.get(pick.slotId) : undefined;
      return age === undefined ? policies : { ...(policies ?? {}), minAge: age };
    }),
  );
}

/** The press-refusal at the box — rests quiet, arrives on PAY (like the name's). */
export const ACKNOWLEDGE_REFUSAL = "Tick this to confirm before you pay — it’s required for these tickets.";

/* ------------------------------------------------------------------ B603 */

/** The three typed identity fields the Pay press can refuse. */
export type BuyerRefusalField = "name" | "email" | "phone";

/** The slice of a zod field this module consults — structural, so nothing
 * here imports a validation library it does not own. */
interface FieldShapeLike {
  safeParse(value: unknown): { success: true } | { success: false; error: { issues: readonly { code?: string }[] } };
}

/**
 * B603 — R19: a typed value is judged for what the field SAYS, against the
 * checkout contract's OWN field schemas: the schema that would refuse the
 * call (`lib/api.ts` parses every body before the wire) is the schema
 * consulted here, so there is no second grammar to drift. Emptiness stays
 * the press's question (B415/B545 — the classic walk may pay with no
 * email, a ruled stance), so this module only ever refuses what was TYPED,
 * trimmed first (a whitespace value carries no information).
 */
const checkoutField = (name: "buyerEmail" | "buyerName" | "buyerPhone"): FieldShapeLike =>
  (cartCheckout.request as unknown as { shape: Record<string, FieldShapeLike> }).shape[name]!;

export const BUYER_EMAIL_PROBLEM =
  "That email doesn’t look complete — it should look like you@example.com. Your tickets are sent there.";
export const BUYER_NAME_PROBLEM = "That name is too long for a booking — please shorten it.";
export const BUYER_PHONE_SHORT = "That phone number looks too short — check it, or leave it empty.";
export const BUYER_PHONE_LONG = "That phone number looks too long — check it, or leave it empty.";
const BUYER_PHONE_PROBLEM = "That phone number doesn’t look right — check it, or leave it empty.";

export interface BuyerFieldProblems {
  readonly name?: string;
  readonly email?: string;
  readonly phone?: string;
}

export function buyerFieldProblems(buyer: BuyerDetails): BuyerFieldProblems {
  const problems: { name?: string; email?: string; phone?: string } = {};
  const name = buyer.name.trim();
  if (name !== "" && !checkoutField("buyerName").safeParse(name).success) problems.name = BUYER_NAME_PROBLEM;
  const email = buyer.email.trim();
  if (email !== "" && !checkoutField("buyerEmail").safeParse(email).success) problems.email = BUYER_EMAIL_PROBLEM;
  const phone = buyer.phone.trim();
  if (phone !== "") {
    const read = checkoutField("buyerPhone").safeParse(phone);
    if (!read.success)
      problems.phone = read.error.issues.some((i) => i.code === "too_big") ? BUYER_PHONE_LONG : BUYER_PHONE_SHORT;
  }
  return problems;
}

/**
 * B603 — the client-schema backstop (R18): `lib/api.ts` refuses a body that
 * does not fit the operation's schema BEFORE the wire (`ApiRequestError`),
 * and until this item both pay-path catches translated that to the
 * page-broken banner — for a value the BUYER typed. A refusal naming a
 * buyer field lands at that field; one naming anything else stays the
 * builder's error, of which `PAGE_MISCONFIGURED` is actually true.
 */
export function buyerFieldOf(fields: readonly string[]): BuyerRefusalField | undefined {
  if (fields.includes("buyerEmail")) return "email";
  if (fields.includes("buyerPhone")) return "phone";
  if (fields.includes("buyerName")) return "name";
  return undefined;
}

/** The backstop's sentence — the field is known, the direction is not. */
export function buyerFieldSentence(field: BuyerRefusalField): string {
  return field === "email" ? BUYER_EMAIL_PROBLEM : field === "name" ? BUYER_NAME_PROBLEM : BUYER_PHONE_PROBLEM;
}

/** FNV-1a, 32-bit — a short stable digest for the key (not a secret). */
function digest(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

export function buyerSetCall(
  orderId: string,
  buyer: BuyerDetails,
  /** B615 leg 2 — the tick rides the write that names the order at Pay. */
  acknowledged = false,
  /** B616 leg 3a — the Insider opt-in rides the same write. */
  subscribe = false,
): {
  readonly body: { orderId: string; buyerEmail: string; buyerName?: string; buyerPhone?: string; acknowledged?: true; subscribe?: true };
  readonly clientKey: string;
} {
  const email = buyer.email.trim();
  const name = buyer.name.trim();
  const phone = buyer.phone.trim();
  return {
    body: {
      orderId,
      buyerEmail: email,
      ...(name !== "" ? { buyerName: name } : {}),
      ...(phone !== "" ? { buyerPhone: phone } : {}),
      ...(acknowledged ? { acknowledged: true as const } : {}),
      ...(subscribe ? { subscribe: true as const } : {}),
    },
    clientKey: `buyer-${orderId}-${digest(`${email.toLowerCase()}\u0000${name}\u0000${phone}${acknowledged ? "\u0000ack" : ""}${subscribe ? "\u0000sub" : ""}`)}`.slice(0, 128),
  };
}
